import { useState } from "react";
import type { MouseEvent } from "react";
import { Menu } from "@material-ui/core";
import { useAddCable, useGetSpareCables } from "../../api/api";
import type { Id, StaticEquipment, Cable, OperationEquipment } from "../../types/models";
import {
  MooringBit,
  Spare,
  getLineStartingPoint,
  MenuItemsArrangements,
  MenuItemsCables,
  useGetGroupedEquipment,
  Winch,
} from "./helpers";

type Arrangement = {
  name: string;
  lineOrder: string[];
  spread: { x: number; y: number };
  point: { x: number; y: number };
  vector: { x: number; y: number };
  color: string;
};

type DeckConfig = {
  direction: string;
  arrangements: Arrangement[];
  arrangementsStb: Arrangement[];
  deck: string;
};

const DeckPlan = ({
  config,
  canEdit,
  shipId,
  operationId,
  equipment,
  side,
}: {
  config: DeckConfig;
  canEdit: boolean;
  shipId: Id;
  operationId: Id | undefined;
  equipment: OperationEquipment[];
  side: string;
}) => {
  const groupedEquipment = useGetGroupedEquipment(shipId, config.direction);
  // Lines passable from a mooring bit = spare-assigned OR unassigned cables.
  // Sourced from /cables/spares because unassigned cables aren't on any
  // static equipment and so never appear in the grouped equipment above.
  const { data: spareCables = [] } = useGetSpareCables(shipId);

  const stationsAndLocations = groupedEquipment.map((group) => {
    const first = group[0];
    return {
      x: Number(first?.position_on_deck?.x ?? 0),
      y: Number(first?.position_on_deck?.y ?? 0),
      stations: group,
    };
  });

  const arrangement =
    side === "PORT" ? config.arrangements : config.arrangementsStb;

  const cables = arrangement.flatMap((arr) => {
    return equipment
      .filter((x): x is OperationEquipment & { winch: StaticEquipment } =>
        x.arrangement === arr.name && x.winch !== undefined
      )
      .map((cable) => {
        const locationName =
          cable.winch.type === "spare" ? "spare" : (cable.winch.station_name ?? "");
        return {
          ...cable,
          priority: arr.lineOrder.indexOf(locationName),
        };
      })
      .sort((lhs, rhs) => lhs.priority - rhs.priority)
      .map((cable, index) => {
        const stroke = `L${
          arr.point.x + index * arr.spread.x
        },${arr.point.y + index * arr.spread.y} l${
          arr.vector.x
        },${arr.vector.y}`;

        return (
          <path
            key={String(cable.id ?? "")}
            fill="none"
            stroke={arr.color}
            strokeWidth="1"
            d={`M${getLineStartingPoint(cable)} ${stroke}`}
          />
        );
      });
  });

  const arrangementNames = config.arrangements.map((x) => x.name);

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [stations, setStations] = useState<StaticEquipment[]>([]);
  const [selection, setSelection] = useState<{ cable: Cable; station: StaticEquipment } | undefined>();
  const handleClose = () => {
    setAnchorEl(null);
    setSelection(undefined);
  };

  const addCable = useAddCable(shipId, operationId ?? "");

  const portsideLine = "M0 1 100 1";
  const starboardLine = "M0 99 100 99";

  return (
    <div>
      <svg viewBox="0 0 100 100" style={{ width: "100%" }}>
        <path
          fill="#CCCCCC"
          stroke="gray"
          strokeWidth="2"
          d={side === "PORT" ? portsideLine : starboardLine}
        />
        <path fill="#CCCCCC" stroke="gray" strokeWidth="1" d={config.deck} />
        {cables}
        {stationsAndLocations.map((sal) => {
          const isMooringBit = sal.stations.some(
            (station) => station?.type === "mooring_bit"
          );
          const isSpare = sal.stations.some((station) => station?.type === "spare");

          const key = `${sal.x},${sal.y}`;
          const handleClick = (event: MouseEvent) => {
            if (canEdit) {
              setStations(sal.stations);
              setAnchorEl(event.currentTarget as Element);
            }
          };

          if (isMooringBit) {
            return <MooringBit key={key} x={sal.x} y={sal.y} onClick={handleClick} />;
          }

          if (isSpare) {
            return <Spare key={key} x={sal.x} y={sal.y} onClick={handleClick} />;
          }

          return <Winch key={key} x={sal.x} y={sal.y} onClick={handleClick} />;
        })}
      </svg>
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {selection ? (
          <MenuItemsArrangements
            arrangements={arrangementNames}
            onArrangementSelect={(arrangement) => {
              setAnchorEl(null);
              setSelection(undefined);
              addCable.mutate({
                arrangement,
                cable_id: selection.cable.id,
                ...(selection.station?.type === "mooring_bit"
                  ? { mooring_bit_id: selection.station.id }
                  : {}),
              });
            }}
          />
        ) : (
          <MenuItemsCables
            disabledCableIds={equipment.map((x) => x?.cable?.id).filter((id): id is Id => id !== undefined)}
            stations={stations}
            spareCables={spareCables}
            onCableSelect={(cable, station) => {
              setSelection({ cable, station });
            }}
          />
        )}
      </Menu>
    </div>
  );
};

export default DeckPlan;
